<template>
  <div>
    <div class="wz-page-header">
      <h1 class="wz-page-title">Horarios de trabajo</h1>
    </div>

    <wz-table-container>

      <wz-table-toolbar>
        <template #left>
          <wz-filter-menu
            :options="filterOptions"
            :selected-option-filter="filters"
            @setFilters="setFilters"
          />
        </template>

        <template #right>
          <wz-tooltip text="Recargar">
            <wz-button variant="outline" @click="loadData()">
              <i class="ri-loop-right-line"></i>
            </wz-button>
          </wz-tooltip>
          <wz-search-bar
            placeholder="Buscar por nombre o código"
            @input="search = $event"
            @search="search = $event"
          />
          <wz-button variant="outline" :disabled="downloadingTemplate" @click="downloadTemplate">
            <i class="ri-download-line mr-1"></i>{{ downloadingTemplate ? 'Descargando...' : 'Descargar plantilla' }}
          </wz-button>
          <wz-button variant="outline" @click="openUploadModal">
            <i class="ri-upload-2-line mr-1"></i>Cargar Excel
          </wz-button>
          <wz-button variant="primary" @click="$router.push({ name: 'work-schedule-create' })">Agregar</wz-button>
        </template>

        <template #filters v-if="showFiltersList">
          <wz-badge
            v-for="(val, name) in filterList"
            :key="name"
            variant="primary"
            pill
            dismissible
            @dismiss="deleteFilter(name)"
          >
            {{ getFilterName(name, val) }}
          </wz-badge>
        </template>
      </wz-table-toolbar>

      <wz-table>
        <thead>
          <tr>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'name' }" @click="toggleSort('name')">
              Nombre <i :class="['wz-th-sort-icon', sortIcon('name')]"></i>
            </th>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'entryTime' }" @click="toggleSort('entryTime')">
              Entrada <i :class="['wz-th-sort-icon', sortIcon('entryTime')]"></i>
            </th>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'exitTime' }" @click="toggleSort('exitTime')">
              Salida <i :class="['wz-th-sort-icon', sortIcon('exitTime')]"></i>
            </th>
            <th>Días laborables</th>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'effectiveHours' }" @click="toggleSort('effectiveHours')">
              H. efectivas <i :class="['wz-th-sort-icon', sortIcon('effectiveHours')]"></i>
            </th>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'toleranceMinutes' }" @click="toggleSort('toleranceMinutes')">
              Tolerancia <i :class="['wz-th-sort-icon', sortIcon('toleranceMinutes')]"></i>
            </th>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'status' }" @click="toggleSort('status')">
              Estado <i :class="['wz-th-sort-icon', sortIcon('status')]"></i>
            </th>
            <th>Opciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="filteredList.length === 0">
            <td colspan="8" class="text-center text-muted py-4">No se encontraron resultados.</td>
          </tr>
          <tr v-for="o in sortedList(filteredList)" :key="o.id">
            <td>
              {{ o.name }}
              <small v-if="o.code" class="block text-muted">{{ o.code }}</small>
            </td>
            <td>{{ o.entryTime }}</td>
            <td>{{ o.exitTime }}</td>
            <td>
              <wz-badge
                v-for="d in (o.workDaysList || [])"
                :key="d"
                variant="info-solid"
                pill
                class="mr-1"
              >{{ dayShort(d) }}</wz-badge>
            </td>
            <td style="text-align: right;">{{ o.effectiveHours }}h</td>
            <td style="text-align: right;">{{ o.toleranceMinutes }} min</td>
            <td>
              <wz-badge :variant="statusVariant(o.status)" pill>
                {{ getStatus(o.status).label }}
              </wz-badge>
            </td>
            <td style="text-align: center;">
              <wz-dropdown
                :items="[
                  { key: 'edit',   label: 'Editar' },
                  { key: 'status', label: getStatus(o.status).action },
                ]"
                placement="left"
                @click="handleAction(o, $event)"
              />
            </td>
          </tr>
        </tbody>
      </wz-table>

      <wz-table-footer>
        <span>{{ filteredList.length }} registro(s)</span>
      </wz-table-footer>

    </wz-table-container>

    <wz-confirm-modal
      v-model="confirmModal.show"
      :title="confirmModal.title"
      :description="confirmModal.description"
      :accept-text="confirmModal.acceptText"
      :cancel-text="confirmModal.cancelText"
      :accept-variant="confirmModal.acceptVariant"
      :alert-variant="confirmModal.alertVariant"
      @confirm="onModalConfirm"
      @cancel="closeConfirmModal"
    />

    <!-- Modal: Cargar horarios desde Excel -->
    <wz-modal v-model="showUploadModal" title="Cargar horarios desde Excel" size="md" :close-on-overlay="false">
      <p class="text-muted mb-3">
        Descargue la plantilla, complétela y súbala. Se crea o actualiza cada horario según su CODIGO.
        Si alguna fila tiene error, no se crea ni actualiza ningún horario del archivo.
      </p>

      <wz-file-input
        id-file="work-schedule-bulk-file"
        :accept-file="['.xlsx']"
        @onChange="onUploadFileChange"
      />

      <wz-alert v-if="uploadErrors.length > 0" variant="danger" icon="ri-error-warning-line" class="mt-3">
        <strong>Se encontraron {{ uploadErrors.length }} error(es). No se guardó ningún horario.</strong>
        <ul class="m-0 pl-3 mt-1">
          <li v-for="(e, i) in uploadErrors" :key="i">
            <template v-if="e.row">Fila {{ e.row }}: </template>{{ e.message }}
          </li>
        </ul>
      </wz-alert>

      <template #footer>
        <wz-button variant="outline" @click="showUploadModal = false">Cancelar</wz-button>
        <wz-button variant="primary" :disabled="!uploadFile || uploading" @click="submitUpload">
          {{ uploading ? 'Subiendo...' : 'Subir' }}
        </wz-button>
      </template>
    </wz-modal>
  </div>
</template>

<script>
import { Alert } from "@wizepe/wizeui"
import { Util } from "@wizepe/wizeui"
import { FilterMixin } from "@wizepe/wizeui"
import { SortMixin, ConfirmModalMixin } from '@wizepe/wizeui'
import { Constants as LocalConstants } from '@/core/utils'
import { WorkScheduleService } from '@/core/services'

const WORK_DAYS = {
  1: { short: 'Lun' },
  2: { short: 'Mar' },
  3: { short: 'Mié' },
  4: { short: 'Jue' },
  5: { short: 'Vie' },
  6: { short: 'Sáb' },
  7: { short: 'Dom' },
}

export default {
  mixins: [SortMixin, FilterMixin, ConfirmModalMixin],
  data() {
    return {
      list: [],
      search: '',
      selectedOption: null,
      downloadingTemplate: false,
      showUploadModal: false,
      uploadFile: null,
      uploading: false,
      uploadErrors: [],
      filterOptions: [
        {
          label: 'Estado',
          key: 'status',
          type: 'SINGLE',
          options: [LocalConstants.WorkScheduleStatus.ACTIVE, LocalConstants.WorkScheduleStatus.INACTIVE],
        },
      ],
    }
  },
  computed: {
    filteredList() {
      let result = this.list
      if (this.filters.status != null) {
        result = result.filter(o => o.status === this.filters.status)
      }
      if (this.search) {
        const norm = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
        const q = norm(this.search)
        result = result.filter(o => norm(o.name).includes(q) || norm(o.code).includes(q))
      }
      return result
    },
  },
  methods: {
    statusVariant(status) {
      return this.getStatus(status)?.color || 'dark'
    },
    handleAction(o, item) {
      if (item.key === 'edit')   this.$router.push({ name: 'work-schedule-edit', params: { id: o.id } })
      if (item.key === 'status') this.openChangeStatus(o)
    },
    async loadData() {
      try {
        await this.$store.dispatch('app/loading', true)
        const resp = await WorkScheduleService.list()
        this.list = (resp.payload || []).map(i => ({ ...i }))
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al cargar los horarios')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    getStatus(key) {
      return Util.searchValue(parseInt(key), LocalConstants.WorkScheduleStatus)
    },
    async downloadTemplate() {
      try {
        this.downloadingTemplate = true
        const resp = await WorkScheduleService.downloadBulkTemplate()
        const url = URL.createObjectURL(new Blob([resp.data]))
        const a = document.createElement('a')
        a.href = url
        a.download = 'plantilla-horarios.xlsx'
        a.click()
        URL.revokeObjectURL(url)
      } catch (e) {
        Alert.error('Error al descargar la plantilla')
      } finally {
        this.downloadingTemplate = false
      }
    },
    openUploadModal() {
      this.uploadFile = null
      this.uploadErrors = []
      this.showUploadModal = true
    },
    onUploadFileChange(files) {
      this.uploadErrors = []
      this.uploadFile = Array.isArray(files) ? files[0] : files
    },
    async submitUpload() {
      if (!this.uploadFile) return
      try {
        this.uploading = true
        this.uploadErrors = []
        const resp = await WorkScheduleService.uploadBulkExcel(this.uploadFile)
        const created = resp && resp.created != null ? resp.created : 0
        const updated = resp && resp.updated != null ? resp.updated : 0
        const warnings = resp && Array.isArray(resp.warnings) ? resp.warnings : []
        let msg = `Horarios guardados: ${created} creado(s), ${updated} actualizado(s)`
        if (warnings.length) {
          msg += '. Advertencias: ' + warnings.map(w => `Fila ${w.row}: ${w.message}`).join('; ')
        }
        Alert.success(msg)
        this.showUploadModal = false
        await this.loadData()
      } catch (error) {
        // El backend responde 400 con { errors: [{ row, message }] } cuando alguna fila falla
        if (error && Array.isArray(error.errors)) {
          this.uploadErrors = error.errors
        } else {
          Alert.error(error && error.message ? error.message : 'Error al cargar el archivo')
        }
      } finally {
        this.uploading = false
      }
    },
    dayShort(num) {
      return (WORK_DAYS[num] || {}).short || String(num)
    },
    openChangeStatus(o) {
      this.selectedOption = o
      this.openConfirmModal({
        title:       'Cambiar estado',
        description: `¿Está seguro que desea ${this.getStatus(o.status).action?.toLowerCase()} este horario?`,
        acceptText:  'Aceptar',
        action:      'update',
      })
    },
    onModalConfirm() {
      const action = this.confirmModal.action
      this.closeConfirmModal()
      if (action === 'update') this.changeStatus()
    },
    async changeStatus() {
      try {
        await this.$store.dispatch('app/loading', true)
        const current   = parseInt(this.selectedOption.status)
        const newStatus = current === LocalConstants.WorkScheduleStatus.ACTIVE.key
          ? LocalConstants.WorkScheduleStatus.INACTIVE.key
          : LocalConstants.WorkScheduleStatus.ACTIVE.key
        await WorkScheduleService.changeStatus(this.selectedOption.id, newStatus)
        await this.loadData()
        Alert.success('Estado actualizado correctamente')
      } catch (ex) {
        console.error(ex)
        Alert.error(ex.errors ? ex.errors.message : 'Error al cambiar el estado')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
  },
  async created() {
    await this.loadData()
  },
}
</script>

<style lang="stylus">
</style>
